import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Mail, Lock, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/qtk9sn35_MEDX-AI-Welcome-Poster.pptx%20%281%29.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { adminLogin } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const seedAdmin = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/seed/admin`);
      if (res.data.email && res.data.password) {
        toast.success(`Admin created! Email: ${res.data.email}, Password: ${res.data.password}`);
        setFormData({ email: res.data.email, password: res.data.password });
      } else {
        toast.info(res.data.message || 'Admin ready');
      }
    } catch (error) {
      toast.info('Admin already exists or ready to use');
    } finally {
      setSeeding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await adminLogin(formData.email, formData.password);
      toast.success('Welcome, Admin!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDF8F8] via-[#FFF5F5] to-[#FDF8F8] relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 hero-glow opacity-30" />
      
      <div className="relative z-10 min-h-screen flex flex-col px-6 py-8">
        {/* Back button */}
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-8"
          data-testid="admin-back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Home
        </button>

        <div className="flex-1 flex flex-col items-center justify-center max-w-md mx-auto w-full">
          {/* Logo */}
          <div className="mb-4" data-testid="admin-login-logo">
            <Link to="/">
              <img src={LOGO_URL} alt="AfriPerx" className="h-14 w-auto" />
            </Link>
          </div>

          {/* Admin badge */}
          <div className="flex items-center gap-2 bg-amber-50 text-afriperx-gold px-4 py-2 rounded-full mb-8 border border-amber-200">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-medium">Admin Portal</span>
          </div>

          {/* Title */}
          <h1 className="font-syne text-3xl md:text-4xl font-bold text-gray-900 mb-2 text-center">
            Admin Access
          </h1>
          <p className="text-gray-500 mb-8 text-center">
            Secure login for administrators
          </p>

          {/* Seed button for demo */}
          <Button
            type="button"
            variant="outline"
            onClick={seedAdmin}
            disabled={seeding}
            className="mb-6 border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            data-testid="seed-admin-btn"
          >
            {seeding ? 'Creating...' : 'Create Test Admin (Demo)'}
          </Button>

          {/* Form */}
          <form onSubmit={handleSubmit} className="w-full space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-2">Username</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    name="email"
                    type="text"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="admin"
                    required
                    className="pl-12 h-12 bg-white border-gray-200 focus:border-afriperx-gold text-gray-900 placeholder:text-gray-400"
                    data-testid="admin-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter admin password"
                    required
                    className="pl-12 h-12 bg-white border-gray-200 focus:border-afriperx-gold text-gray-900 placeholder:text-gray-400"
                    data-testid="admin-password-input"
                  />
                </div>
              </div>
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary h-12 text-base"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Access Admin Portal'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};
